import { TextInput, Select, Button, SelectOptionType } from '@octano/global-ui';
import { useCallback } from 'react';
import { Col, Container, Row } from 'reactstrap';
import { useForm, FormProvider } from 'react-hook-form';
import {
  CourseEnrollment,
  CreateValidationFields,
  CreateValidationSubmitValues,
} from '../../types';
import useTextValidationType from '../../../../components/curriculum/hooks/useTextValidationType';
import { CourseValidationType } from '../../../../types/CourseValidationType';
import RAPValidationForm from './RAPValidationForm';
import InternalValidationForm from './InternalValidationForm';
import { convertFieldsToSubmit, convertSubmitToFields } from '../../utils';
import ExternalValidationForm from './ExternalValidationForm';
import { Institution } from '../../../Configurations/Institutions/interfaces/type';

export interface ValidationFormProps {
  defaultValues?: CreateValidationSubmitValues;
  courseEnrollments?: CourseEnrollment[];
  institutions?: Institution[];
  validationTypeOptions?: SelectOptionType[];
  validationSubTypeOptions?: SelectOptionType[];
  courseName?: string;
  courseCode?: string;
  loading?: boolean;
  readonly?: boolean;
  onCancel?: () => void;
  onConfirm?: (values: CreateValidationSubmitValues) => void;
}

const ValidationForm = ({
  defaultValues,
  courseEnrollments = [],
  institutions = [],
  validationTypeOptions = [],
  validationSubTypeOptions = [],
  courseName,
  courseCode,
  loading = false,
  readonly = false,
  onCancel,
  onConfirm,
}: ValidationFormProps) => {
  const { texts } = useTextValidationType();

  const form = useForm<CreateValidationFields>({
    defaultValues: defaultValues
      ? convertSubmitToFields({
          submitValues: defaultValues,
          validationTypeOptions,
          validationSubTypeOptions,
          institutions,
          courseEnrollments,
        })
      : undefined,
  });
  const { control, watch } = form;

  const [selectedType] = watch(['validationType']);

  const handleSubmit = useCallback(
    (values: CreateValidationFields) => {
      if (loading) {
        return;
      }

      !!onConfirm && onConfirm(convertFieldsToSubmit(values));
    },
    [loading, onConfirm],
  );

  return (
    <FormProvider {...form}>
      <Container>
        <Row>
          {!!courseName && (
            <Col xs={12}>
              <TextInput
                name="courseName"
                label={texts.courseNameLabel}
                value={courseName}
                disabled
              />
            </Col>
          )}
          <Col xs={12}>
            <Select
              name="validationType"
              label={texts.validationType.selectLabel}
              options={validationTypeOptions}
              control={control}
              disabled={readonly}
              rules={{ required: texts.required }}
            />
          </Col>
        </Row>
        {selectedType?.value === CourseValidationType.RAP && (
          <RAPValidationForm
            readonly={readonly}
            validationSubTypeOptions={validationSubTypeOptions}
          />
        )}
        {selectedType?.value === CourseValidationType.External && (
          <ExternalValidationForm
            readonly={readonly}
            institutions={institutions}
          />
        )}
        {selectedType?.value === CourseValidationType.Internal && (
          <InternalValidationForm
            readonly={readonly}
            courseCode={courseCode}
            courseName={courseName}
            courseEnrollments={courseEnrollments}
          />
        )}
        <Row>
          <Col xs={readonly ? 12 : 6}>
            <Button
              type="button"
              onClick={onCancel}
              text={texts.actions.cancel}
              outlined
              fullwidth
            />
          </Col>
          {!readonly && (
            <Col xs={6}>
              <Button
                type="button"
                text={texts.actions.confirm}
                fullwidth
                loading={loading}
                onClick={form.handleSubmit(handleSubmit)}
              />
            </Col>
          )}
        </Row>
      </Container>
    </FormProvider>
  );
};

export default ValidationForm;
